import { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DashboardService } from '../services/dashboard';
import { EnergyConsumption } from '../models/DashboardDetails';

const MONTH_LABEL_KEYS: Record<string, string> = {
  Jan: 'equipment.monthShort.Jan',
  Fev: 'equipment.monthShort.Fev',
  Mar: 'equipment.monthShort.Mar',
  Abr: 'equipment.monthShort.Abr',
  Mai: 'equipment.monthShort.Mai',
  Jun: 'equipment.monthShort.Jun',
  Jul: 'equipment.monthShort.Jul',
  Ago: 'equipment.monthShort.Ago',
  Set: 'equipment.monthShort.Set',
  Out: 'equipment.monthShort.Out',
  Nov: 'equipment.monthShort.Nov',
  Dez: 'equipment.monthShort.Dez',
};

type Props = {
  scopeType: 'group' | 'location';
  scopeId?: number;
};

export const useEnergyConsumption = ({ scopeType, scopeId }: Props) => {
  const intl = useIntl();
  const [data, setData] = useState<EnergyConsumption.Response | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [period, setPeriod] = useState<'week' | 'month'>('week');
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!scopeId) return;

    let mounted = true;
    setLoading(true);

    const fetchEnergy = async () => {
      try {
        const apiScopeType = scopeType === 'group' ? 'STORE' : 'LOCATION';
        
        const response = await DashboardService.getEnergyConsumption({
          scopeType: apiScopeType,
          scopeId,
          period
        });

        if (mounted) {
          setData(response);
          setError(false);
        }
      } catch (err) {
        console.error('Failed to load energy stats:', err);
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchEnergy();

    return () => { mounted = false; };
  }, [scopeType, scopeId, period]);

  const handlePeriodChange = useCallback((id: string, value: string) => {
    if (value === 'week' || value === 'month') {
      setPeriod(value);
    }
  }, []);

  const translateChartLabel = useCallback((label: string) => {
    const [month, weekLabel] = label.split(' - Semana ');
    const monthId = MONTH_LABEL_KEYS[month];
    const translatedMonth = monthId
      ? intl.formatMessage({ id: monthId, defaultMessage: month })
      : month;

    if (!weekLabel) return translatedMonth;

    return `${translatedMonth} - ${intl.formatMessage({ id: 'equipment.Semana' })} ${weekLabel}`;
  }, [intl]);

  const chartConfig = useMemo(() => {
    if (!data?.chartData) return { labels: [], datasets: [] };

    return {
      labels: data.chartData.map(d => translateChartLabel(d.label)),
      datasets: [
        {
          label: 'Consumo',
          data: data.chartData.map(d => d.consumption),
          backgroundColor: '#4da6ff',
          borderRadius: 8,
          stack: 'stack1',
        },
        {
          label: 'Economia ECO',
          data: data.chartData.map(d => d.eco),
          backgroundColor: '#33cc33',
          borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
          stack: 'stack1',
        }
      ]
    };
  }, [data, translateChartLabel]);

  const hasToro = !!data;

  const hasLogs = useMemo(() => {
    if (!data) return false;
    return data.accumulatedConsumption > 0 || data.chartData.length > 0;
  }, [data]);

  return {
    loading,
    error,
    hasToro,
    hasLogs,
    summary: {
      connected: data?.connectedCount ?? 0,
      accumulated: data?.accumulatedConsumption ?? 0,
      estimated: data?.estimatedConsumption ?? 0,
      savings: data?.ecoSavings ?? 0,
      avgWeekly: data?.averageWeekly ?? 0,
      avgMonthly: data?.averageMonthly ?? 0
    },
    chartConfig,
    period,
    handlePeriodChange
  };
};
